import {
  ColDef,
  GridOptions,
  ICellRendererParams,
  themeAlpine,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo, useRef } from "react";

import {
  IgAzurePrimitives,
  IgGrayPrimitives,
} from "@intelligems/ig-design-tokens/constants";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types/functions/analytics/analyze-resource-tinybird/analyze-resource-tinybird";
import { CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import { productGroupTableFormatter } from "@src/api/analytics/dashboard-tinybird";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { useSelector } from "@src/store";
import { selectedVariations } from "@src/store/slices/variations";
import { theme } from "@src/theme";
import { UseQueryResult } from "@tanstack/react-query";

const myTheme = themeAlpine.withParams({
  fontFamily: theme.typography.fontFamily,
  rowBorder: `1px solid ${IgGrayPrimitives[600]}`,
  headerRowBorder: `1px solid ${IgGrayPrimitives[600]}`,
  headerFontWeight: 600,
  headerTextColor: IgGrayPrimitives["A300"],
  headerFontSize: "14px",
  wrapperBorder: `1px solid ${IgGrayPrimitives[600]}`,
  wrapperBorderRadius: "8px",
  borderRadius: "8px",
});

interface ProductGroupsTableProps {
  selectedMetricId: string;
  data: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
}

const ConversionLoadingOverlay = (props: ICellRendererParams) => {
  return (
    <Grid container direction={"column"}>
      <Grid>
        <CircularProgress size={20} variant="indeterminate" />
      </Grid>
      <Grid>
        <IgTypography>Data loading... please wait</IgTypography>
      </Grid>
    </Grid>
  );
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .ag-header-cell:first-of-type`]: {
    borderRight: `1px solid ${theme.igPrimitives.gray[600]}`,
    justifyContent: "center !important",
  },
  [`& .ag-header-row`]: {
    backgroundColor: IgAzurePrimitives[100],
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px",
  },
  [`& .ag-header-cell-text`]: {
    color: `${theme.palette.text.secondary} !important`,
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "18px",
    letterSpacing: ".25px",
  },
  [`& .ag-header-cell-comp-wrapper`]: {
    justifyContent: "center !important", // centering grouped headers
  },
  // [`& .ag-cell-norm`]: {
  [`& .ag-cell:first-of-type`]: {
    borderRight: `1px solid ${theme.igPrimitives.gray[600]}`,
    fontSize: "10px",
  },
  // },
  [`& .ag-cell-numeric`]: {
    [`&.ag-cell`]: {
      textAlign: "center",
      fontSize: "14px",
      lineHeight: "16.8px",
      [`&.ag-cell:not(:last-child)`]: {
        borderRight: `1px solid ${theme.igPrimitives.gray[600]}`,
      },
    },
  },
  [`& .ag-cell-wrapper.ag-row-group`]: {
    alignItems: "center !important",
  },
  [`& .ag-center-cols-viewport`]: {
    minHeight: "10px !important",
  },
}));

export const ProductGroupsTable = ({
  selectedMetricId,
  data,
}: ProductGroupsTableProps) => {
  const graphValueFormatter = useGraphValueFormatter();
  const gridRef = useRef<AgGridReact>(null);
  const gridOptions = useMemo<GridOptions>(() => {
    return {
      tooltipInteraction: true,
    };
  }, []);
  const variations = useSelector(selectedVariations);
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      resizable: false,
      cellStyle: {
        fontSize: "14px",
        alignContent: "center",
        // color: ColorSecondary.main,
      },
      menuTabs: [],
      suppressMovable: true,
      wrapHeaderText: true,
      suppressHeaderMenuButton: true,
      sortable: false,
    };
  }, []);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: "productGroup",
        headerName: "Product Group",
        minWidth: 150,
        flex: 1,
        cellStyle: {
          fontSize: "14px",
        },
      },
      ...variations.map((variation) => {
        return {
          field: variation.id,
          headerName: variation.name,
          minWidth: 120,
          cellClass: "ag-cell-norm",
          cellStyle: {
            fontSize: "14px",
          },
          valueFormatter: (params) => {
            if (!params.value) {
              return "--";
            } else if (selectedMetricId === "units_per_order") {
              return graphValueFormatter({
                isAxisTick: true,
                value: params.value,
                configDataTypes: ["number"],
              })!;
            } else {
              return graphValueFormatter({
                isAxisTick: false,
                value: params.value,
                configDataTypes: ["currency"],
              })!;
            }
          },
        };
      }),
    ],
    [selectedMetricId]
  );

  const filteredMetricRowData = useMemo(() => {
    if (data[0].isLoading) {
      return null;
    } else {
      return productGroupTableFormatter(
        data[0].data?.metrics || [],
        selectedMetricId
      );
    }
  }, [selectedMetricId, data]);

  return (
    <StyledGrid
      sx={{
        marginTop: "2px",
        width: "100%",
        // paddingLeft: "0px !important",
        paddingRight: "0px !important",
      }}
    >
      <AgGridReact
        ref={gridRef}
        theme={myTheme}
        defaultColDef={defaultColDef}
        rowData={filteredMetricRowData}
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        suppressDragLeaveHidesColumns={true}
        suppressExcelExport={true}
        suppressCsvExport={true}
        suppressContextMenu={true}
        suppressCellFocus={true}
        loadingOverlayComponent={ConversionLoadingOverlay}
        headerHeight={38}
        rowHeight={70}
        domLayout={"autoHeight"}
        loading={data[0].isLoading}
      />
    </StyledGrid>
  );
};
